/**
 * What the Foundry area says, in a person's words: kinds, identities, states, and the little helpers its three places share
 * (D441). Nothing here names a route or a package.
 */

import { useEffect, useSyncExternalStore } from 'react';
import type { FoundryConnection, FoundryConnectionRequest } from '@aioe/shared';

import { hasDirectory, resolveSubjects, subjectName, subscribeNames } from '../../directory.ts';

export const KIND_WORDS: Record<FoundryConnection['kind'], string> = { azure: 'Azure', 'azure-local': 'Azure Local' };
export const IDENTITY_WORDS: Record<FoundryConnection['identity'], string> = { person: 'Each person’s own sign-in', organisation: 'The organisation’s sign-in' };

/** "Runs as each person's own sign-in", as a clause after a dot. */
export const runsAs = (identity: FoundryConnection['identity']) => (identity === 'person' ? 'runs as each person’s own sign-in' : 'runs as the organisation');

export const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

/** The address without its scheme, for a line of small print. */
export function hostOf(endpoint: string): string {
  try {
    const u = new URL(endpoint);
    return `${u.host}${u.pathname === '/' ? '' : u.pathname}`;
  } catch {
    return endpoint;
  }
}

/** How many Foundry projects a connection approves (an Azure Local cluster counts as one place). */
export const projectCount = (c: FoundryConnection) => (c.kind === 'azure' ? (c.projects?.length ?? 0) : 1);

/** What a request is for, in a few words. */
export const requestTarget = (q: FoundryConnectionRequest) => (q.kind === 'azure' ? (q.project?.name ?? hostOf(q.endpoint)) : `${q.azureLocal?.cluster ?? 'a cluster'} (Azure Local)`);

/** What a request asks to use: the deployments (or models) it names. */
export function requestUses(q: FoundryConnectionRequest): string[] {
  return q.kind === 'azure' ? (q.project?.deployments ?? []) : (q.azureLocal?.models ?? []);
}

/** A person's name from the directory when it knows them, else undefined (never a raw id). */
export function usePersonName(id: string | undefined): string | undefined {
  const s = useSyncExternalStore(subscribeNames, () => (id ? subjectName(id) : undefined));
  useEffect(() => {
    if (id && hasDirectory()) void resolveSubjects([id]);
  }, [id]);
  return s && !s.missing && s.name !== id ? s.name : undefined;
}

export const AGENT_STATE_WORDS = { listed: 'Listed', held: 'Waiting for review', rejected: 'Rejected', withdrawn: 'Withdrawn' } as const;
export const AGENT_STATE_MARK = { listed: 'connected', held: 'medium', rejected: 'disabled', withdrawn: 'offline' } as const;
export type AgentState = keyof typeof AGENT_STATE_WORDS;
export const ROLE_WORDS = { specialist: 'as a specialist', member: 'as a team member' } as const;
